/* Vue liste des contacts (alternative au tableau). */
import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import type { AccountState, CrmContact } from './types';
import { ENTITY_LABELS, ROLE_BADGES, ROLE_LABELS, STAGES, STAGE_INDEX, displayName, isGroupCard } from './types';
import { type GroupStats, rolesSummary } from './participants';
import { missingForNextStage } from './validation';
import { StatusDot, formatDateFr } from './ui';

type SortKey = 'name' | 'stage' | 'updated';

interface Props {
  contacts: CrmContact[];
  accounts: Record<string, AccountState>;
  groupStats: Record<string, GroupStats>;
  onOpen: (c: CrmContact) => void;
}

export function ContactListView({ contacts, accounts, groupStats, onOpen }: Props) {
  const [sort, setSort] = useState<SortKey>('stage');

  const sorted = [...contacts].sort((a, b) => {
    if (sort === 'name') return displayName(a).localeCompare(displayName(b));
    if (sort === 'updated') return b.updated_at.localeCompare(a.updated_at);
    return STAGE_INDEX[a.stage] - STAGE_INDEX[b.stage] || displayName(a).localeCompare(displayName(b));
  });

  const header = (key: SortKey, label: string) => (
    <button onClick={() => setSort(key)} className={`flex items-center gap-1 uppercase ${sort === key ? 'text-neutral-900' : ''}`}>
      {label}{sort === key && <ChevronDown className="w-3 h-3" />}
    </button>
  );

  return (
    <div className="flex-1 overflow-auto p-4">
      <div className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-neutral-50 border-b border-neutral-100 text-[10px] font-semibold text-neutral-500 uppercase tracking-wide">
              <th className="text-left px-4 py-2.5">{header('name', 'Nom')}</th>
              <th className="text-left px-3 py-2.5">Type</th>
              <th className="text-left px-3 py-2.5">{header('stage', 'Étape')}</th>
              <th className="text-left px-3 py-2.5">E-mail</th>
              <th className="text-left px-3 py-2.5">EAN</th>
              <th className="text-left px-3 py-2.5">À compléter</th>
              <th className="text-left px-3 py-2.5">Plateforme</th>
              <th className="text-left px-3 py-2.5">{header('updated', 'Modifié')}</th>
            </tr>
          </thead>
          <tbody>
            {sorted.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-10 text-neutral-400">Aucun contact</td></tr>
            ) : sorted.map(c => {
              const stage = STAGES.find(s => s.key === c.stage)!;
              const missing = missingForNextStage(c);
              const group = isGroupCard(c);
              const stats = group && c.groupe ? groupStats[c.groupe.trim().toLowerCase()] : undefined;
              return (
                <tr key={c.id} onClick={() => onOpen(c)} className="border-b border-neutral-50 hover:bg-amber-50/40 cursor-pointer">
                  <td className="px-4 py-2.5">
                    <p className="font-medium text-neutral-800">{displayName(c)}</p>
                    <p className="text-[10px] text-neutral-400">
                      {group ? `Groupe${stats?.companies ? ` · ${stats.companies} société${stats.companies > 1 ? 's' : ''}` : ''}` : c.entity_type ? ENTITY_LABELS[c.entity_type] : '—'}
                      {c.source === 'formulaire' ? ' · formulaire' : ''}
                      {c.merged_into_id ? ` · rattaché à ${c.groupe}` : c.archived_at ? ' · archivé' : ''}
                      {c.stage !== 'active' && c.waiting_on === 'sunisup' && <span className="text-amber-700 font-medium"> · à traiter</span>}
                      {c.stage !== 'active' && c.waiting_on === 'client' && ' · attente client'}
                    </p>
                  </td>
                  <td className="px-3 py-2.5">
                    {group && stats
                      ? <span className="text-[10px] text-neutral-600">{rolesSummary(stats.roles)}</span>
                      : c.role ? <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-semibold ${ROLE_BADGES[c.role]}`}>{ROLE_LABELS[c.role]}</span> : '—'}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className="inline-flex items-center gap-1.5 text-neutral-700"><span className={`w-2 h-2 rounded-full ${stage.dot}`} />{stage.label}</span>
                  </td>
                  <td className="px-3 py-2.5 text-neutral-600">{c.email || '—'}</td>
                  <td className="px-3 py-2.5 text-neutral-500">
                    {/* Code EAN en premier ; le nom de la fiche participant (parfois une adresse) en second. */}
                    {group
                      ? `${stats?.eans ?? c.meters.length} EAN`
                      : c.meters.length === 0 ? '—' : c.meters.length === 1
                        ? (
                          <>
                            <span className="block font-mono text-[10px] text-neutral-700">{c.meters[0].ean_code || '—'}</span>
                            {c.meters[0].name?.trim() && <span className="block text-[10px] text-neutral-400 truncate max-w-[180px]" title={c.meters[0].name}>{c.meters[0].name}</span>}
                          </>
                        )
                        : <span title={c.meters.map(m => m.ean_code).filter(Boolean).join('\n')}>{c.meters.length} EAN</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {missing.length > 0
                      ? <span className="text-amber-700" title={missing.map(m => m.label).join(', ')}>{missing.length} champ{missing.length > 1 ? 's' : ''}</span>
                      : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5">
                    {c.stage === 'active' ? (
                      <div className="flex gap-2">
                        <StatusDot on={accounts[c.id] === 'approved'} pending={accounts[c.id] === 'pending'} label="Compte" />
                        <StatusDot on={!!c.onboarding_sent_at} label="Onboarding" />
                      </div>
                    ) : <span className="text-neutral-300">—</span>}
                  </td>
                  <td className="px-3 py-2.5 text-neutral-400">{formatDateFr(c.updated_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
